// Right-click menu for task rows — surface-driven actions from taskContextMenuConfig.
import { useState, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import DeleteConfirmDialog from '@/features/organize/components/DeleteConfirmDialog';
import { getEnabledMenuActions } from '@/features/tasks/components/context-menu/taskContextMenuConfig';
import {
  getDayBoundaries,
  getTodayTasks,
} from '@/features/tasks/utils/views/dailySectionsUtils';
import { computeMoveToTodayUpdates } from '@/features/today/utils/todayListReorderUtils';
import { applyTodayTaskUpdates } from '@/features/tasks/utils/interactions/mobileDndUtils';
import { TaskContextMenuPrimary } from './TaskContextMenuMenus';
import { TaskContextMenuAssign } from './TaskContextMenuAssign';

/**
 * @param {object} props
 * @param {import('@/types').Task|null} props.task
 * @param {import('@/features/tasks/components/context-menu/taskContextMenuConfig').TaskContextMenuSurface|null} props.surface
 * @param {{ mouseX: number, mouseY: number }|null} props.anchorPosition
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(parentId: string) => void} [props.onStartAddingSubtask]
 */
export default function TaskContextMenu({
  task,
  surface,
  anchorPosition,
  open,
  onClose,
  onStartAddingSubtask,
}) {
  const { user } = useAuth();
  const { personDisplayMap, filteredTasks, tasksMap } = useTasksContext();
  const { updateTask, deleteTask } = useTaskMutationsContext();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const actions = useMemo(() => getEnabledMenuActions(surface), [surface]);
  const personOptions = useMemo(
    () =>
      Array.from(personDisplayMap?.values() ?? []).sort((a, b) =>
        a.localeCompare(b),
      ),
    [personDisplayMap],
  );

  const closeAll = useCallback(() => {
    setAssignOpen(false);
    onClose();
  }, [onClose]);

  const handleMoveToTomorrow = useCallback(async () => {
    if (!task?.id || !user?.uid || task.completed) return;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    await updateTask(
      task.id,
      { date: format(tomorrow, 'yyyy-MM-dd') },
      user.uid,
    );
    closeAll();
  }, [task, user, updateTask, closeAll]);

  const handleMoveToToday = useCallback(async () => {
    if (!task?.id || !user?.uid || task.completed) return;
    const { startOfToday, endOfToday } = getDayBoundaries();
    const todayTasks = getTodayTasks(
      filteredTasks,
      startOfToday,
      endOfToday,
      tasksMap,
    );
    const updates = computeMoveToTodayUpdates({ todayTasks, activeTask: task });
    applyTodayTaskUpdates(updates, user, updateTask);
    closeAll();
  }, [task, user, filteredTasks, tasksMap, updateTask, closeAll]);

  const handleMoveToBacklog = useCallback(async () => {
    if (!task?.id || !user?.uid || task.completed || task.status === 'Backlog')
      return;
    await updateTask(task.id, { status: 'Backlog' }, user.uid);
    closeAll();
  }, [task, user, updateTask, closeAll]);

  const handleMoveToActive = useCallback(async () => {
    if (!task?.id || !user?.uid || task.completed || task.status !== 'Backlog')
      return;
    await updateTask(task.id, { status: 'Active' }, user.uid);
    closeAll();
  }, [task, user, updateTask, closeAll]);

  const handleAssignPerson = useCallback(
    async (person) => {
      if (!task?.id || !user?.uid) return;
      await updateTask(task.id, { person: person || null }, user.uid);
      closeAll();
    },
    [task, user, updateTask, closeAll],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!task?.id) return;
    await deleteTask(task.id);
    setDeleteOpen(false);
    closeAll();
  }, [task, deleteTask, closeAll]);

  if (!task || !surface || actions.length === 0) return null;

  return (
    <>
      <TaskContextMenuPrimary
        task={task}
        actions={actions}
        open={open}
        anchorPosition={anchorPosition}
        onClose={closeAll}
        onOpenAssign={() => setAssignOpen(true)}
        onMoveToTomorrow={handleMoveToTomorrow}
        onMoveToToday={handleMoveToToday}
        onMoveToBacklog={handleMoveToBacklog}
        onMoveToActive={handleMoveToActive}
        onAddSubtask={() => {
          onStartAddingSubtask?.(task.id);
          closeAll();
        }}
        onDelete={() => setDeleteOpen(true)}
      />

      <TaskContextMenuAssign
        task={task}
        open={assignOpen}
        anchorPosition={anchorPosition}
        personOptions={personOptions}
        onClose={() => setAssignOpen(false)}
        onAssignPerson={handleAssignPerson}
      />

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
